<template>
    <div class="page-create-calendar">
        <template
            v-if="
                !order && calendar_object && calendar_object.has_pending_order
            "
        >
            <el-alert
                :closable="false"
                :title="
                    'Editing for calendar is disabled. Order for ' +
                    currentMonth.format('MMMM YYYY') +
                    ' issue is already submitted.'
                "
                center
                class="text-center"
                show-icon
                style="max-width: 800px; margin: 50px auto"
                type="warning"
            >
            </el-alert>
        </template>
        <div
            v-show="
                order || !calendar_object || !calendar_object.has_pending_order
            "
        >
            <div class="page-header mb-2">
                <div style="position: relative">
                    <h1>
                        {{ mode === "create" ? "Create" : "Edit" }} Calendar
                    </h1>
                    <el-button
                        @click="backToOrder()"
                        icon="mdi mdi-arrow-left"
                        size="small"
                        style="position: absolute"
                        type="text"
                        v-if="order"
                        >Back to Order Details
                    </el-button>
                </div>
                <el-steps class="mt-10" :active="currentStep - 1" align-center>
                    <el-step
                        :description="step.text"
                        @click.self="onClickStep(index + 1)"
                        v-for="(step, index) in steps"
                        :key="step.name"
                        :status="getStepStatus(index)"
                        :class="{ done: currentStep > index + 1 }"
                    >
                        <a
                            href="#"
                            v-if="isStepAccessible(index)"
                            @click.prevent="onClickStep(index + 1)"
                            slot="icon"
                        >
                            <i
                                class="el-step__icon-inner is-status el-icon-check"
                                v-if="currentStep > index + 1"
                            />
                            <span v-else>{{ index + 1 }}</span>
                        </a>
                        <span v-else>
                            {{ index + 1 }}
                        </span>
                        <template slot="description">
                            <a
                                @click.prevent="onClickStep(index + 1)"
                                href="#"
                                title="Click to go to this step"
                                v-if="isStepAccessible(index)"
                            >
                                {{ step.text }}
                            </a>
                            <span v-else>{{ step.text }}</span>
                        </template>
                    </el-step>
                </el-steps>
            </div>

            <div class="action-buttons">
                <el-button
                    :disabled="!canPrev"
                    @click="prev"
                    class="float-left"
                    icon="el-icon-arrow-left"
                    type="default"
                    v-if="currentStep > 1"
                >
                    Previous
                </el-button>

                <template v-if="currentStep === 1">
                    <h3 class="float-left ml-20 mt-7" v-if="currentMonth">
                        {{ currentMonth.format("MMMM YYYY") }}
                        {{
                            calendarLocation
                                ? " - " + data_get(calendarLocation, "name")
                                : ""
                        }}
                    </h3>
                </template>

                <el-button
                    v-if="canNext"
                    :loading="form.busy"
                    @click="next"
                    class="float-right el-button--primary"
                    icon="mdi mdi-content-save"
                    type="primary"
                >
                    Save and Proceed
                </el-button>

                <transition name="el-fade-in">
                    <div
                        class="float-right mr-10 mt-8 success-text"
                        v-show="show_auto_save_message"
                    >
                        <i class="mdi mdi-check-circle" /> Auto-saved
                    </div>
                </transition>
                <transition name="el-fade-in">
                    <div
                        class="float-right mr-10 mt-8 text-error"
                        v-show="show_auto_save_error"
                    >
                        <i class="mdi mdi-alert-circle" /> Can't auto-save
                    </div>
                </transition>
                <transition name="el-fade-in">
                    <div
                        class="float-right mr-10 mt-8 warning-text"
                        v-show="typing_active && hasChanges"
                    >
                        <i class="mdi mdi-keyboard"></i> Typing... auto-save
                        paused
                    </div>
                </transition>
                <div
                    class="float-right mr-10 mt-8 warning-text"
                    v-show="
                        !show_auto_save_error &&
                        !show_auto_save_message &&
                        currentStepName !== CalendarEditSteps.EVENTS.value &&
                        hasChanges
                    "
                >
                    You have unsaved changes.
                </div>

                <div class="float-right" v-if="currentStep === 1">
                    <font-customizer
                        v-if="!isWeekly"
                        :form="form"
                        :font="form.font"
                    />
                </div>

                <div class="clearfix"></div>
            </div>

            <keep-alive>
                <events
                    :calendar="calendar_object"
                    ref="events"
                    :form="form"
                    @date-saved="onDateSaved"
                    @auto-saved="onAutoSaved"
                    v-if="
                        steps[currentStep - 1].name === 'events' &&
                        (!$route.params.id || !!calendar_object) &&
                        form.community_location_id
                    "
                />
            </keep-alive>

            <customize-calendar
                :calendar="calendar_object"
                :form="form"
                ref="customize"
                v-if="steps[currentStep - 1].name === 'customize'"
            />

            <calendar-proof
                ref="proof"
                :with-template="false"
                :calendar="calendar_object"
                v-if="
                    calendar_object && steps[currentStep - 1].name === 'proof'
                "
            />
            <keep-alive>
                <apply-theme
                    :calendar="calendar_object"
                    :form="form"
                    v-if="
                        calendar_object &&
                        steps[currentStep - 1].name === 'theme'
                    "
                />
            </keep-alive>

            <keep-alive>
                <place-order
                    :form="form"
                    :calendar="calendar_object"
                    :order="order"
                    v-if="
                        calendar_object &&
                        steps[currentStep - 1].name === 'place order'
                    "
                />
            </keep-alive>

            <el-dialog
                :close-on-click-modal="false"
                :visible.sync="locationDialogVisible"
                title="Select Community Location"
                width="400px"
            >
                <el-form @submit.native.prevent style="max-width: 500px">
                    <el-form-item
                        class="mb-0 mt-20"
                        label="Community Location:"
                        :class="form.state('community_location_id')"
                    >
                        <el-select
                            name="community_location_id"
                            v-model="form.community_location_id"
                            filterable
                            placeholder="Select Location..."
                            data-vv-as="community location"
                            v-validate="'required'"
                        >
                            <el-option
                                v-for="item in locations"
                                :key="item.id"
                                :label="item.name"
                                :value="item.id"
                            >
                            </el-option>
                        </el-select>
                        <form-feedback
                            :form="form"
                            field="community_location_id"
                        />
                    </el-form-item>
                </el-form>
                <div slot="footer" class="dialog-footer">
                    <el-button @click="locationDialogVisible = false"
                        >Cancel</el-button
                    >
                    <el-button
                        type="primary"
                        :loading="form.busy"
                        @click="submitLocation()"
                        >Save & Proceed</el-button
                    >
                </div>
            </el-dialog>
        </div>
    </div>
</template>

<script>
import * as BusEvents from "@/constants/bus-events.js";
import CalendarEditSteps from "@/constants/calendar-edit-steps";
import * as Fonts from "@/constants/calendar-fonts";
import * as ROLES from "@/constants/roles";
import * as Routes from "@/constants/routes.js";
import address_mixin from "@/mixins/address";
import FontLoader from "@/services/FontLoader";
import * as Actions from "@/store/constants/actions";
import CalendarUtils, { CalendarCalculator } from "@/utils/calendar";
import { mapGetters, mapState } from "vuex";
import ApplyTheme from "./single/ApplyTheme";
import CalendarProof from "./single/CalendarProof";
import CustomizeCalendar from "./single/CustomizeCalendar";
import Events from "./single/Events";
import FontCustomizer from "./single/FontCustomizer";
import LocationKey from "./single/LocationKey";
import PlaceOrder from "./single/PlaceOrder";
import Environment from "@/utils/Environment";

export default {
    mixins: [address_mixin],
    components: {
        LocationKey,
        Events,
        FontCustomizer,
        CustomizeCalendar,
        CalendarProof,
        ApplyTheme,
        PlaceOrder
    },
    props: {
        mode: {
            type: String,
            default: "create"
        },
        calendar: {
            type: Object,
            default: null
        },
        order: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            Fonts,
            CalendarEditSteps,
            currentStep: 1,
            form: new Form({
                is_weekly: false,
                community_location_id: null,
                title: null,
                title_color: "#000000",
                font: this.$store.state.config.settings.font.default_name,
                font_size: this.$store.state.config.settings.font.default_size,
                logo: null,
                logo_src: null,
                remove_logo: false,
                customer_approved: false,
                theme_id: null,
                year: null,
                month: null,
                events: [],
                current_step: null,
                custom_text: null,
                custom_text_lines: [],
                location_keys: [],
                dates: [],
                proofed: false,
                update_title: false
            }),
            events_screenshot: null,
            calendar_object: null,
            locationDialogVisible: false,
            submitLocationCallback: null,
            oldData: null,
            eventsSnapshot: [],

            auto_save_interval: null,
            auto_saving: false,
            show_auto_save_message: false,
            show_auto_save_error: false,
            typing_active: false
        };
    },
    computed: {
        ...mapState({
            progress_steps: (state) => state.enums.calendar_progress_steps
        }),
        ...mapGetters(["userRoles", "hasAnyRole"]),
        current_community() {
            if (this.calendar) {
                return this.calendar.community;
            }
            return this.$store.state.current_community;
        },
        steps() {
            let steps = [
                {
                    name: CalendarEditSteps.EVENTS.value,
                    text: "Events"
                },
                {
                    name: CalendarEditSteps.CUSTOMIZE.value,
                    text: "Customize My Calendar"
                },
                {
                    name: CalendarEditSteps.PROOF.value,
                    text: "Proof My Calendar"
                },
                {
                    name: CalendarEditSteps.THEME.value,
                    text: "Choose My Artwork/Theme"
                },
                {
                    name: CalendarEditSteps.PLACE_ORDER.value,
                    text: this.order ? "Edit Quantities" : "Place My Order"
                }
            ];
            if (this.isWeekly) {
                steps = _.filter(steps, (item) => {
                    return !_.includes(
                        [
                            CalendarEditSteps.CUSTOMIZE.value,
                            CalendarEditSteps.THEME.value
                        ],
                        item.name
                    );
                });
            }
            if (this.order) {
                steps = _.reject(steps, {
                    name: CalendarEditSteps.PLACE_ORDER.value
                });
            }
            return steps;
        },
        currentStepName() {
            return this.steps[this.currentStep - 1].name;
        },
        canNext() {
            return this.currentStep < this.steps.length;
        },
        canPrev() {
            return this.currentStep > 1;
        },
        locations() {
            if (!this.current_community) {
                return [];
            }
            return this.current_community.locations.data;
        },
        currentMonth() {
            if (this.calendar_object) {
                if (!this.form.year) {
                    return null;
                }
                return moment([this.form.year, this.form.month - 1]);
            }
            if (this.current_community) {
                return moment([
                    this.current_community.current_month.year,
                    this.current_community.current_month.month - 1
                ]);
            }
            return moment().add(1, "month");
        },
        calendarLocation() {
            return _.find(this.locations, {
                id: this.form.community_location_id
            });
        },
        isWeekly() {
            return _.get(
                this.calendar_object,
                "is_weekly",
                _.get(this.$route, "query.is_weekly", false) == 1
            );
        },
        hasChanges() {
            //console.log(this.oldData, this.getCleanFormData());
            return this.oldData && this.oldData !== this.getCleanFormData();
        },
        calendarCalculator() {
            if (!this.form.year) {
                return null;
            }
            return new CalendarCalculator(
                _.pick(this.form, ["month", "year", "font", "font_size"])
            );
        },
        // Add a new computed property to check if user is staff
        isStaffUser() {
            return this.hasAnyRole(ROLES.MAIN_ROLES);
        },
        // Add development mode detection
        isDevelopment() {
            return Environment.isDevelopment();
        },

        // Check if theme requirement should be bypassed
        shouldBypassThemeRequirement() {
            return Environment.shouldBypassThemeRequirement();
        }
    },
    created() {
        window.Bus.$once("error:order_pending_for_current_month", () => {
            this.stopAutoSave();
        });
    },
    mounted() {
        this.form.setVeeErrors(this.veeErrors);
        let initOldData = () => {
            Vue.nextTick(() => {
                this.initOldData();
            });
        };
        if (this.calendar) {
            this.calendar_object = this.calendar;
            this.initCalendar().then(() => {
                initOldData();
            });
        } else {
            let moment = window.moment([
                this.current_community.current_month.year,
                this.current_community.current_month.month - 1
            ]);
            this.form.year = moment.year();
            this.form.month = moment.month() + 1;
            //if no this.$route.query.location_id, then redirect to home
            if (!this.$route.query.location_id) {
                this.$router.push({ name: "Home" });
            }
            this.form.community_location_id = this.$route.query.location_id;

            this.form.is_weekly = this.$route.query.is_weekly == 1;
            this.initDefaultFont();
            initOldData();
        }
        this.initAutoSave();
        //if step parameter is set, then to that step
        if (this.$route.query.step) {
            this.currentStep = parseInt(this.$route.query.step);
        }
    },
    beforeDestroy() {
        this.stopAutoSave();
    },
    beforeRouteLeave(to, from, next) {
        this.stopAutoSave();
        next();
    },
    methods: {
        initOldData() {
            this.oldData = this.getCleanFormData();
        },
        initDefaultFont() {
            window.axios
                .get(`/web-api/calendar/current-font`, {
                    params: {
                        community_location_id: this.form.community_location_id
                    }
                })
                .then((response) => {
                    this.form.font = response.data.font;
                    this.form.font_size = response.data.font_size;
                });
        },
        getCleanFormData() {
            let data = _.pick(this.form, [
                "customer_approved",
                "font",
                "font_size",
                "year",
                "month",
                "theme_id",
                "current_step",
                "title",
                "title_color",
                "remove_logo",
                "community_location_id",
                "custom_text",
                "custom_text_lines",
                "is_weekly",
                "logo",
                "remove_logo",
                "location_keys"
            ]);
            if (this.$refs.events) {
                this.eventsSnapshot = this.$refs.events.getCleanEvents();
            }
            data.events = this.eventsSnapshot;
            return JSON.stringify(data);
        },
        submitLocation() {
            this.$validator.validate().then((valid) => {
                if (!valid) {
                    return;
                }
                this.submitLocationCallback();
            });
        },
        onClickStep(step) {
            const currentStepName = this.steps[this.currentStep - 1].name;
            const targetStepName = this.steps[step - 1].name;

            // Ensure proper refresh when changing steps
            this.ensureProperRefreshOnStepChange(
                currentStepName,
                targetStepName
            );

            switch (this.currentStepName) {
                case CalendarEditSteps.EVENTS.value:
                    this.checkMaxedOutDates()
                        .then(() => {
                            this.currentStep = step;
                        })
                        .catch((error) => {
                            console.log(error);
                        });
                    break;
                case CalendarEditSteps.CUSTOMIZE.value:
                    this.checkedCustomTextLines()
                        .then(() => {
                            this.currentStep = step;
                        })
                        .catch((error) => {
                            console.log(error);
                        });
                    break;
                default:
                    this.currentStep = step;
                    break;
            }
        },
        checkMaxedOutDates() {
            return new Promise((resolve, reject) => {
                if (this.$refs.events && this.$refs.events.hasMaxedOutDate) {
                    if (this.isStaffUser) {
                        // Show confirmation with Cancel button for staff
                        this.$confirm(
                            "There are maxed out dates. As a staff user, you can proceed, but please review before finalizing.",
                            "Warning",
                            {
                                confirmButtonText: "Proceed",
                                cancelButtonText: "Cancel",
                                type: "warning"
                            }
                        )
                            .then(() => {
                                // Staff user chose to proceed
                                console.log(
                                    "Staff user bypassing maxed out dates check"
                                );
                                resolve();
                            })
                            .catch(() => {
                                // Staff user cancelled
                                reject(
                                    new Error(
                                        "Staff user cancelled the operation"
                                    )
                                );
                            });
                    } else {
                        // Regular users still get a normal alert
                        this.$alert(
                            "Please fix the maxed out dates.",
                            "Warning",
                            {
                                type: "warning",
                                callback: () => {
                                    // Regular users must fix the issues
                                    reject(
                                        new Error("Please fix maxed out date")
                                    );
                                }
                            }
                        );
                    }
                } else {
                    resolve();
                }
            });
        },
        checkedCustomTextLines() {
            return new Promise((resolve, reject) => {
                if (
                    this.$refs.customize &&
                    this.$refs.customize.hasExceededMaxLines
                ) {
                    if (this.isStaffUser) {
                        // Show confirmation with Cancel button for staff
                        this.$confirm(
                            "There are custom text lines that exceed the maximum. As a staff user, you can proceed, but please review before finalizing.",
                            "Warning",
                            {
                                confirmButtonText: "Proceed",
                                cancelButtonText: "Cancel",
                                type: "warning"
                            }
                        )
                            .then(() => {
                                // Staff user chose to proceed
                                console.log(
                                    "Staff user bypassing custom text lines check"
                                );
                                resolve();
                            })
                            .catch(() => {
                                // Staff user cancelled
                                reject(
                                    new Error(
                                        "Staff user cancelled the operation"
                                    )
                                );
                            });
                    } else {
                        // Regular users still get a normal alert
                        this.$alert(
                            "Please fix the maxed out custom text lines.",
                            "Warning",
                            {
                                type: "warning",
                                callback: () => {
                                    // Regular users must fix the issues
                                    reject(
                                        new Error(
                                            "Please fix maxed out custom text lines"
                                        )
                                    );
                                }
                            }
                        );
                    }
                } else {
                    resolve();
                }
            });
        },
        next() {
            let updateCurrentStep = (response) => {
                if (this.calendar_object) {
                    _.extend(
                        this.calendar_object,
                        _.pick(response.data, [
                            "current_step",
                            "pricing",
                            "proofed"
                        ])
                    );
                }
                if (response.data.formatted_logo) {
                    this.form.logo_src = response.data.formatted_logo.url;
                }

                this.currentStep++;
                this.initOldData();
            };
            switch (this.currentStepName) {
                case CalendarEditSteps.EVENTS.value:
                    this.checkMaxedOutDates()
                        .then(() => {
                            if (!this.calendar_object) {
                                if (!this.form.community_location_id) {
                                    if (this.locations.length > 0) {
                                        this.locationDialogVisible = true;
                                        this.submitLocationCallback = () => {
                                            this.saveEvents()
                                                .then((response) => {
                                                    this.locationDialogVisible = false;
                                                    return response;
                                                })
                                                .then(updateCurrentStep);
                                        };
                                        return;
                                    } else {
                                        this.form.community_location_id = _.get(
                                            this.locations,
                                            "0.id"
                                        );
                                    }
                                }
                            }
                            this.saveEvents().then(updateCurrentStep);
                        })
                        .catch((error) => {
                            console.log(error);
                        });
                    break;
                case CalendarEditSteps.CUSTOMIZE.value:
                    this.checkedCustomTextLines()
                        .then(() => {
                            this.saveCustomize().then(updateCurrentStep);
                        })
                        .catch((error) => {
                            console.log(error);
                        });
                    break;
                case CalendarEditSteps.PROOF.value:
                    this.saveProof().then(updateCurrentStep);
                    break;
                case CalendarEditSteps.THEME.value:
                    // Allow bypassing theme requirement in development
                    if (
                        !this.form.theme_id &&
                        !this.shouldBypassThemeRequirement
                    ) {
                        this.form.errors.add(
                            "theme_id",
                            "Please select a theme to continue."
                        );
                        return;
                    }

                    // Show development warning if bypassing theme requirement
                    if (
                        !this.form.theme_id &&
                        this.shouldBypassThemeRequirement
                    ) {
                        console.warn(
                            "Development mode: Proceeding without theme selection"
                        );
                        if (Environment.shouldShowDevMessages()) {
                            this.$message({
                                message:
                                    "Development mode: Proceeding with blank template",
                                type: "info",
                                duration: 3000
                            });
                        }
                    }

                    this.form.errors.forget();
                    this.saveTheme().then(updateCurrentStep);
                    break;
                default:
                    this.currentStep++;
                    break;
            }
        },
        prev() {
            this.currentStep--;
        },
        setCurrentStep() {
            this.form.current_step = null;
            if (typeof this.progress_steps[this.currentStep] !== "undefined") {
                this.form.current_step = this.progress_steps[this.currentStep];
            }
        },
        onDateSaved() {
            this.saveEvents().then(() => {
                this.initOldData();

                // Give enough time for network operations to settle
                setTimeout(() => {
                    if (this.$refs.events && this.$refs.events.$refs.calendar) {
                        this.$refs.events.$refs.calendar.refreshCalendarDisplay(
                            {
                                caller: "Single.onDateSaved",
                                forceFullRefresh: true
                            }
                        );
                    }
                }, 150);
            });
        },
        onAutoSaved() {
            this.initOldData();
        },
        saveEvents() {
            let post_method = !this.calendar_object;
            return new Promise((resolve, reject) => {
                this.form.events = this.$refs.events.getCleanEvents();
                this.form.dates = this.$refs.events.getDates();
                this.setCurrentStep();
                if (!post_method) {
                    let fields = [
                        "font",
                        "font_size",
                        "year",
                        "month",
                        "current_step",
                        "community_location_id",
                        "dates",
                        "events"
                    ];
                    //console.log(this.form);
                    App.put(
                        `/web-api/calendar/${this.calendar_object.id}?include=events.deleted_events,location_keys`,
                        this.form,
                        null,
                        fields
                    )
                        .then((response) => {
                            _.extend(this.calendar_object, response.data);
                            resolve(response);
                        })
                        .catch((errors) => {
                            console.log(errors);
                            reject(errors);
                        });
                } else {
                    //console.log(this.form);
                    App.post(
                        `/web-api/calendar?include=events.deleted_events,location_keys,formatted_logo`,
                        this.form,
                        null,
                        [
                            "title",
                            "events",
                            "font",
                            "font_size",
                            "year",
                            "month",
                            "community_location_id",
                            "is_weekly",
                            "dates"
                        ]
                    )
                        .then((response) => {
                            this.$router.push({
                                name: "CreateCalendar",
                                query: {
                                    id: response.data.id
                                }
                            });
                            this.calendar_object = response.data;
                            resolve(response);
                        })
                        .catch((error) => {
                            if (
                                _.get(error, "data.error") ===
                                "calendar_already_exists"
                            ) {
                                this.$alert(
                                    `Calendar already exist for ${this.currentMonth.format(
                                        "MMMM YYYY"
                                    )} issue. Please edit the existing calendar instead.`,
                                    "Oops!",
                                    {
                                        confirmButtonText: "OK",
                                        type: "warning",
                                        callback: (action) => {
                                            this.initOldData();
                                            this.$router.push({
                                                name: Routes.EDIT_CALENDAR,
                                                params: {
                                                    id: error.data.data
                                                        .existing_calendar.id
                                                }
                                            });
                                            reject(error);
                                        }
                                    }
                                );
                            } else {
                                reject(error);
                            }
                        });
                }
            }).then((response) => {
                this.initCalendar(post_method).then(() => {
                    Bus.$emit(BusEvents.CALENDAR_EVENTS_SAVED, response.data);
                });
                return response;
            });
        },
        saveCustomize() {
            this.setCurrentStep();
            this.$refs.customize.updateLocationKeyValueLines();

            // Validate location keys before saving
            const invalidKeys = this.form.location_keys.filter((key) => {
                return (
                    key.key &&
                    key.key.trim() &&
                    (!key.value_lines || key.value_lines.length === 0)
                );
            });

            if (invalidKeys.length > 0) {
                console.log("Found invalid location keys, fixing before save");
                invalidKeys.forEach((key) => {
                    // Ensure we have valid value_lines for each key that has content
                    if (key.value && key.value.trim()) {
                        key.value_lines =
                            this.$refs.customize.calendarCalculator.getLocationKeyValueLines(
                                key
                            );
                    }
                });

                setTimeout(() => {
                    this.saveCustomize()
                        .then(() => {
                            this.auto_saving = false;
                            this.show_auto_save_message = true;
                            this.show_auto_save_error = false;
                            this.initOldData();
                            setTimeout(() => {
                                this.show_auto_save_message = false;
                            }, 3000);
                        })
                        .catch((e) => {
                            this.auto_saving = false;
                            this.show_auto_save_error = true;
                            setTimeout(() => {
                                this.show_auto_save_error = false;
                            }, 3000);
                        });
                }, 500);
                return null;
            }

            let valid_font_name = CalendarUtils.getValidFontName(
                this.form.font,
                true
            );
            this.form.custom_text_lines = Utils.getWrappedLines(
                this.form.custom_text,
                valid_font_name,
                this.form.font_size,
                CalendarUtils.getFontWeight(valid_font_name),
                this.$store.state.config.settings.calendar.custom_text_max_width
            );
            return App.putData(
                `/web-api/calendar/${this.calendar_object.id}?include=formatted_logo,pricing`,
                this.form,
                (form_data) => {
                    if (Utils.emptyOrNull(this.form.custom_text)) {
                        form_data.append("remove_custom_text", 1);
                    }
                    if (!this.form.location_keys.length) {
                        form_data.append("clear_location_keys", 1);
                    }
                },
                [
                    "logo",
                    "customer_approved",
                    "current_step",
                    "location_keys",
                    "logo",
                    "title",
                    "update_title",
                    "remove_logo",
                    "custom_text",
                    "custom_text_lines"
                ]
            ).then((response) => {
                if (this.$store.state.current_community) {
                    this.$store.dispatch(Actions.FETCH_CURRENT_COMMUNITY);
                }
                this.form.logo = null;
                return response;
            });
        },
        saveProof() {
            this.setCurrentStep();
            this.form.proofed = true;
            return App.putData(
                `/web-api/calendar/${this.calendar_object.id}`,
                this.form,
                null,
                ["current_step", "proofed"]
            );
        },
        saveTheme() {
            this.setCurrentStep();
            //if the title color is selected as white make it black
            //    if(this.form.title_color == '#FFFFFF'){
            //      this.form.title_color = '#000000';
            //}
            return App.put(
                `/web-api/calendar/${this.calendar_object.id}`,
                this.form,
                null,
                [
                    "theme_id",
                    "current_step",
                    "title_color",
                    "title_outline_color"
                ]
            );
        },
        async initCalendar(update_event_ids = false) {
            return new Promise((resolve) => {
                _.extend(
                    this.form,
                    _.pick(this.calendar_object, [
                        "customer_approved",
                        "font",
                        "font_size",
                        "year",
                        "month",
                        "theme_id",
                        "current_step",
                        "title",
                        "title_color",
                        "title_outline_color",
                        "remove_logo",
                        "community_location_id",
                        "custom_text",
                        "custom_text_lines",
                        "is_weekly",
                        "proofed"
                    ])
                );
                if (this.calendar_object.formatted_logo) {
                    this.form.logo_src =
                        this.calendar_object.formatted_logo.url;
                }
                this.form.location_keys = [];
                _.forEach(
                    _.get(this.calendar_object, "location_keys.data"),
                    (location_key) => {
                        this.form.location_keys.push(
                            _.pick(location_key, [
                                "id",
                                "key",
                                "value",
                                "color",
                                "value_lines"
                            ])
                        );
                    }
                );
                Vue.nextTick(async () => {
                    if (this.$refs.events) {
                        this.$refs.events.initCalendar(
                            this.calendar_object,
                            update_event_ids
                        );
                    }
                    if (this.calendar_object && this.calendar_object.font) {
                        await FontLoader.loadFont(this.calendar_object.font);
                    }
                    resolve();
                });
            });
        },
        getStepStatus(i) {
            if (i + 1 === this.currentStep) {
                return "process";
            } else if (i + 1 < this.currentStep) {
                return null;
            }
            return "wait";
        },
        isStepAccessible(index) {
            let proof_step_index = this.steps.indexOf(
                _.find(this.steps, { name: "proof" })
            );
            if (
                this.calendar_object &&
                !this.calendar_object.proofed &&
                index > proof_step_index
            ) {
                return false;
            }
            return (
                this.data_get(this.calendar_object, "current_step") &&
                this.progress_steps.indexOf(
                    this.calendar_object.current_step
                ) >= index
            );
        },
        /**
         * Auto-saves changes every 60 seconds.
         */
        initAutoSave() {
            //console.log("auto save started");
            this.stopAutoSave();

            // Skip autosave setup for staff users
            if (this.isStaffUser) {
                console.log("Autosave disabled for staff user");
                return;
            }
            this.auto_save_interval = setInterval(() => {
                if (this.form.busy) {
                    return;
                }
                if (!this.hasChanges) {
                    return;
                }

                // Check if user is actively typing
                if (
                    this.lastTypingTimestamp &&
                    Date.now() - this.lastTypingTimestamp < 3000
                ) {
                    console.log(
                        "User is actively typing, postponing auto-save"
                    );
                    return;
                }
                let promise = this.autoSave();
                if (promise) {
                    promise.catch((e) => {
                        console.log("Auto save error", e);
                        this.auto_saving = false;
                        this.show_auto_save_error = true;
                        setTimeout(() => {
                            this.show_auto_save_error = false;
                        }, 3000);
                    });
                }
            }, 60 * 1000);
        },
        stopAutoSave() {
            //console.log("auto save stopped");
            clearInterval(this.auto_save_interval);
        },
        autoSave() {
            if (this.auto_saving) {
                return null;
            }

            // Set auto-saving flag
            this.auto_saving = true;
            this.show_auto_save_message = false;
            this.show_auto_save_error = false;

            let promise = null;

            // Use the appropriate save method based on the current step
            switch (this.currentStepName) {
                case CalendarEditSteps.EVENTS.value:
                    if (this.$refs.events) {
                        promise = this.saveEvents();
                    }
                    break;
                case CalendarEditSteps.CUSTOMIZE.value:
                    if (this.$refs.customize) {
                        // Validate location keys before saving
                        this.$refs.customize.updateLocationKeyValueLines();

                        // Check for invalid location keys and fix them
                        const hasInvalidKeys = this.form.location_keys.some(
                            (key) =>
                                key.key &&
                                key.key.trim() &&
                                (!key.value_lines ||
                                    key.value_lines.length === 0)
                        );

                        if (hasInvalidKeys) {
                            // Wait a moment to allow value_lines to be updated
                            setTimeout(() => {
                                this.saveCustomize()
                                    .then(() => {
                                        this.auto_saving = false;
                                        this.show_auto_save_message = true;
                                        this.show_auto_save_error = false;
                                        this.initOldData();
                                        setTimeout(() => {
                                            this.show_auto_save_message = false;
                                        }, 3000);
                                    })
                                    .catch((e) => {
                                        this.auto_saving = false;
                                        this.show_auto_save_error = true;
                                        setTimeout(() => {
                                            this.show_auto_save_error = false;
                                        }, 3000);
                                    });
                            }, 500);
                            return null;
                        } else {
                            promise = this.saveCustomize();
                        }
                    }
                    break;
                case CalendarEditSteps.THEME.value:
                    if (this.form.theme_id) {
                        //user needs to manually check the proof
                        //promise = this.saveTheme();
                    }
                    break;
                case CalendarEditSteps.PROOF.value:
                    if (this.$refs.proof) {
                        //user needs to manually check the proof
                        //promise = this.saveProof();
                    }
                    break;
            }

            if (promise) {
                return promise
                    .then(() => {
                        this.auto_saving = false;
                        this.show_auto_save_message = true;
                        this.show_auto_save_error = false; // Add this line to clear any error message
                        this.initOldData();
                        setTimeout(() => {
                            this.show_auto_save_message = false;
                        }, 3000);
                    })
                    .catch((e) => {
                        console.log("Auto save error", e);
                        this.auto_saving = false;
                        this.show_auto_save_error = true;
                        setTimeout(() => {
                            this.show_auto_save_error = false;
                        }, 3000);
                    });
            }

            this.auto_saving = false;
            return null;
        },
        backToOrder() {
            this.$router.push({
                name: Routes.VIEW_ORDER,
                params: {
                    id: this.order.id
                }
            });
        },
        trackTypingActivity() {
            this.lastTypingTimestamp = Date.now();
            this.typing_active = true;

            // Clear the typing indicator after a delay
            clearTimeout(this._typingTimeout);
            this._typingTimeout = setTimeout(() => {
                this.typing_active = false;
            }, 3000);
        },
        ensureProperRefreshOnStepChange(fromStep, toStep) {
            // When returning to events step from any other step
            if (
                toStep === CalendarEditSteps.EVENTS.value &&
                fromStep !== CalendarEditSteps.EVENTS.value
            ) {
                console.log(
                    "Performing complete calendar refresh when returning to events"
                );

                // Wait for Vue to render the component
                Vue.nextTick(() => {
                    if (this.$refs.events && this.$refs.events.$refs.calendar) {
                        // Use the existing refreshCalendarDisplay method with a specific caller
                        this.$refs.events.$refs.calendar.refreshCalendarDisplay(
                            {
                                caller: "stepChange",
                                forceFullRefresh: true
                            }
                        );

                        // If any additional processing is needed after the refresh
                        setTimeout(() => {
                            if (
                                this.$refs.events &&
                                this.$refs.events.$refs.calendar
                            ) {
                                this.$refs.events.$refs.calendar.$forceUpdate();
                            }
                        }, 100);
                    }
                });
            }
        }
    },
    watch: {
        "form.font": {
            immediate: true,
            handler(newFont) {
                if (newFont) {
                    FontLoader.loadFont(newFont).then(() => {
                        // Clear cache after font changes
                        CalendarUtils.clearDescriptionLinesCache();
                        this.$nextTick(() => {
                            if (this.$refs.events) {
                                this.$refs.events.updateEventDescriptionLines();
                            }
                        });
                    });
                }
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.action-buttons {
    margin-top: 45px;
    margin-bottom: 0;
}
</style>

<style lang="scss" scoped>
@import "../../../../assets/scss/_variables";
.page-create-calendar {
    /*padding-bottom: 100px;*/
    .el-step__description.is-success {
        color: $text-color-success;

        a {
            color: $text-color-success;
        }
    }

    .el-steps {
        flex-grow: 1;

        a {
            font-weight: bold;
            text-decoration: none;
        }

        ::v-deep .el-step__head.is-process .el-step__icon.is-text {
            border-color: $text-color-accent;
            background-color: $text-color-accent;
        }
        ::v-deep .el-step.is-center .el-step__description {
            padding-left: 0;
            padding-right: 0;
        }
    }

    .page-header {
        margin-bottom: 10px;
        display: flex;
    }
}
</style>
